import Divider from '@shared/components/Divider/Divider';
import cx from 'classnames';

import { ReactComponent as Minus } from 'assets/icons/Minus.svg';
import { ReactComponent as Plus } from 'assets/icons/Plus.svg';
import LanguageSelect from 'components/LanguageSelect/LanguageSelect';
import Collapsible from 'modules/Collapsible/Collapsible';
import useCollapsibleContext from 'modules/Collapsible/useCollapsibleContext';
import { SetState } from 'utils/types';

import SidebarButton from './SidebarButton';
import { SettingsModalSize, Tab } from '../SettingsModal';

const sidebarSizes = {
  fullScreen: 'md:max-w-[256px]',
  md: 'md:max-w-[192px]',
};

interface SettingsSidebarProps {
  tabs: Tab[];
  activeTab: number;
  setActiveTab: SetState<number>;
  hasLanguageSelect: boolean;
  size: SettingsModalSize;
}

interface CollapsibleTabHeadProps {
  icon: Tab['icon'];
  title: string;
}

const CollapsibleTabHead = ({ icon: Icon, title }: CollapsibleTabHeadProps) => {
  const { isCollapsed, toggleIsCollapsed } = useCollapsibleContext();

  return (
    <div
      onClick={toggleIsCollapsed}
      className={cx(
        'text-grayscale-gray80 flex w-fit cursor-pointer items-center justify-between rounded-xl p-2.5 md:w-full',
        { '!text-primary-70': !isCollapsed }
      )}
    >
      <div className="flex items-center space-x-2">
        <Icon className="h-5 w-5 shrink-0 stroke-current stroke-2" />
        <span className="text-size-sm hidden truncate font-medium md:flex">{title}</span>
      </div>
      <div className="hidden md:block">
        {isCollapsed ? (
          <Plus className="h-5 w-5 stroke-gray-50 stroke-1.5" />
        ) : (
          <Minus className="h-5 w-5 stroke-current stroke-1.5" />
        )}
      </div>
    </div>
  );
};

interface CollapsibleTabProps {
  tab: Tab;
  activeTab: number;
  setActiveTab: SetState<number>;
}

const CollapsibleTab = ({ tab, activeTab, setActiveTab }: CollapsibleTabProps) => {
  const { subTabs = [] } = tab;
  const isAnySubTabActive = subTabs.some((s) => s.id === activeTab);
  const firstSubTab = subTabs[0];

  return (
    <>
      <div className="md:hidden">
        <SidebarButton
          icon={tab.icon}
          onClick={() => firstSubTab && setActiveTab(firstSubTab.id)}
          isActive={isAnySubTabActive}
        >
          {tab.title}
        </SidebarButton>
      </div>
      <div className="hidden md:block">
        <Collapsible isInitiallyCollapsed={!isAnySubTabActive}>
          <div>
            <CollapsibleTabHead icon={tab.icon} title={tab.title} />
            <Collapsible.Content hasDivider={false} className="!p-0" containerClassName="!space-y-0 pl-[19px]">
              {subTabs.map((sub) => (
                <SidebarButton
                  key={sub.id}
                  icon={sub.icon}
                  onClick={() => setActiveTab(sub.id)}
                  isActive={sub.id === activeTab}
                >
                  {sub.title}
                </SidebarButton>
              ))}
            </Collapsible.Content>
          </div>
        </Collapsible>
      </div>
    </>
  );
};

const SettingsSidebar = ({ tabs, activeTab, setActiveTab, hasLanguageSelect, size }: SettingsSidebarProps) => (
  <div
    className={cx(
      'border-grayscale-gray30 order-2 flex h-fit w-full flex-row items-center justify-center border-t p-2 md:p-4 md:order-1 md:h-full md:flex-col md:items-start md:justify-start md:border-r md:border-t-0',
      sidebarSizes[size]
    )}
  >
    <div className="flex w-fit space-x-2 md:w-full md:flex-col md:space-x-0 md:space-y-2">
      {tabs.map((tab) =>
        tab.subTabs?.length ? (
          <CollapsibleTab key={tab.id} tab={tab} activeTab={activeTab} setActiveTab={setActiveTab} />
        ) : (
          <SidebarButton
            key={tab.id}
            icon={tab.icon}
            onClick={() => setActiveTab(tab.id)}
            isActive={tab.id === activeTab}
            dataTestId={tab.dataTestId}
          >
            {tab.title}
          </SidebarButton>
        )
      )}
    </div>
    {hasLanguageSelect && (
      <>
        <Divider verticalSpace="md" className="mt-auto hidden md:block" />
        <Divider verticalSpace="md" isVertical className="mx-4 block md:hidden" />
        <LanguageSelect flagNeeded />
      </>
    )}
  </div>
);

export default SettingsSidebar;
